import { captureException } from '@sentry/react';
import { queryOptions } from '@tanstack/react-query';
import z from 'zod';

import { authorizedAxiosInstance } from '@/clients';

const GEOCODING_STALE_TIME = 7 * 24 * 60 * 60 * 1_000;

const geocodedLocationSchema = z
    .object({
        country: z.string().optional(),
        countryCode: z.string().optional(),
        latitude: z.number().min(-90).max(90),
        longitude: z.number().min(-180).max(180),
        name: z.string().min(1),
        precision: z.enum(['country', 'locality', 'region']),
        region: z.string().optional(),
    })
    .nullable();

export interface GeocodingLocationInput {
    address?: string;
    city?: string;
    country?: string;
    region?: string;
}

export type GeocodedLocation = z.infer<typeof geocodedLocationSchema>;

const normalizeLocationPart = (value?: string) => value?.trim() ?? '';

export const normalizeGeocodingLocationInput = (location?: GeocodingLocationInput): GeocodingLocationInput => ({
    address: normalizeLocationPart(location?.address) || undefined,
    city: normalizeLocationPart(location?.city) || undefined,
    country: normalizeLocationPart(location?.country) || undefined,
    region: normalizeLocationPart(location?.region) || undefined,
});

export const hasGeocodableLocation = (location?: GeocodingLocationInput) =>
    Boolean(
        normalizeLocationPart(location?.city) ||
        normalizeLocationPart(location?.region) ||
        normalizeLocationPart(location?.country),
    );

const getQueryKeyParts = (location?: GeocodingLocationInput) => {
    const normalizedLocation = normalizeGeocodingLocationInput(location);

    return [
        normalizedLocation.address ?? '',
        normalizedLocation.city ?? '',
        normalizedLocation.region ?? '',
        normalizedLocation.country ?? '',
    ];
};

const fetchGeocodedLocation = async (location: GeocodingLocationInput, signal: AbortSignal) => {
    try {
        const response = await authorizedAxiosInstance.get('/api/vendors/geonames/geocode', {
            params: normalizeGeocodingLocationInput(location),
            signal,
        });

        return geocodedLocationSchema.parse(response.data);
    } catch (error) {
        if (!signal.aborted) {
            captureException(error);
        }

        throw error;
    }
};

export const geocodingQueryKeys = {
    location: (location?: GeocodingLocationInput) =>
        ['vendors', 'geonames', 'geocode', ...getQueryKeyParts(location)] as const,
};

export const geocodingQueryOptions = {
    location: (location: GeocodingLocationInput) =>
        queryOptions({
            queryFn: ({ signal }) => fetchGeocodedLocation(location, signal),
            queryKey: geocodingQueryKeys.location(location),
            retry: 1,
            staleTime: GEOCODING_STALE_TIME,
        }),
};
